// BulkApproveButton — top-right control for approving all ready drafts at once.
// Shows a count badge; clicking opens a confirmation modal before proceeding.
// Drafts with empty commentary are excluded (counted separately by the parent).

import { useState } from "react";
import { Button } from "./Button";

interface Props {
  /** Number of drafts that have non-empty commentary and can be approved */
  draftsWithCommentary: number;
  /** Async handler; resolves when bulk-approve API call completes */
  onConfirm: () => Promise<void>;
}

export function BulkApproveButton({ draftsWithCommentary, onConfirm }: Props) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleConfirm() {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
      setShowConfirm(false);
    }
  }

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        onClick={() => setShowConfirm(true)}
        disabled={draftsWithCommentary === 0}
        title={
          draftsWithCommentary === 0
            ? "No drafts ready — add commentary to at least one draft first"
            : undefined
        }
      >
        Bulk approve ({draftsWithCommentary})
      </Button>

      {showConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-approve-heading"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 200,
          }}
          onClick={(e) => {
            // Close on backdrop click (not if clicking inside the modal)
            if (e.target === e.currentTarget && !busy) setShowConfirm(false);
          }}
        >
          <div
            style={{
              background: "var(--color-ink-800)",
              color: "var(--color-bone)",
              padding: 28,
              borderRadius: "var(--radius-md)",
              maxWidth: 480,
              width: "90%",
              boxShadow: "var(--shadow-md)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <h3
              id="bulk-approve-heading"
              style={{ margin: "0 0 10px", fontSize: "var(--fs-lg)", fontWeight: 700 }}
            >
              Approve {draftsWithCommentary} draft
              {draftsWithCommentary !== 1 ? "s" : ""}?
            </h3>

            <p
              style={{
                fontSize: "var(--fs-sm)",
                color: "var(--color-bone-dim)",
                lineHeight: 1.6,
                margin: "0 0 20px",
              }}
            >
              Drafts with non-empty commentary will be approved and sent to the
              render queue. Drafts with empty commentary are skipped and remain
              in <em>pending review</em>.
            </p>

            <div style={{ display: "flex", gap: 8 }}>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirm}
                disabled={busy}
              >
                {busy ? "Approving…" : "Confirm"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowConfirm(false)}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
